import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Typography, Box, Breadcrumbs, Link, Alert } from '@mui/material';
import { Home } from '@mui/icons-material';
import { EntityList } from '../components/EntityList';
import { AssetInventoryList } from '@/components/assets/AssetInventoryList';
import { LoadingSkeleton } from '../components/shared/LoadingSkeleton';
import { useEntityTypeConfig, useMenuConfig } from '@/hooks';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { isUiConfigEntityPageDisabled } from '@/utils/entityTypeConfig';
import {
  buildPostgrestOrIlikeFilter,
  escapePostgrestQuotedValue,
  normalizePostgrestIlikeTemplate,
} from '@/utils/postgrestFilters';
import { EntityListFilters } from '@/components/entities/EntityListFilters';
import type { EntityListFilterConfig } from '@/types/Entity';
import { logger } from '@equip/utils/logger';

const VALUE_TEMPLATE_PLACEHOLDER = '{{value}}';
const QUOTED_VALUE_TEMPLATE_FRAGMENT = `"*${VALUE_TEMPLATE_PLACEHOLDER}*"`;
const SIMPLE_ILIKE_TEMPLATE = `ilike.${QUOTED_VALUE_TEMPLATE_FRAGMENT}`;

const OR_PARAM = 'or';

/** Detect whether a normalized template is a simple single-field ilike (e.g. ilike."*{{value}}*") */
const isSimpleIlikeTemplate = (normalizedTemplate: string | undefined): boolean =>
  normalizedTemplate === SIMPLE_ILIKE_TEMPLATE;

/**
 * Detect whether a template is a multi-field OR ilike pattern like:
 * (field1.ilike."*{{value}}*",field2.ilike."*{{value}}*",...)
 * Returns the extracted field names if matched, otherwise undefined.
 */
const parseMultiFieldOrIlikeTemplate = (
  template: string | undefined,
  placeholder = VALUE_TEMPLATE_PLACEHOLDER,
): string[] | undefined => {
  if (!template) return undefined;
  const normalized = normalizePostgrestIlikeTemplate(template, placeholder);
  if (!normalized) return undefined;
  // Must start with ( and end with )
  if (!normalized.startsWith('(') || !normalized.endsWith(')')) return undefined;
  const inner = normalized.slice(1, -1);
  const pattern = `ilike."*${placeholder}*"`;
  const fields: string[] = [];
  for (const segment of inner.split(',')) {
    const trimmed = segment.trim();
    if (!trimmed.endsWith(pattern)) return undefined;
    const field = trimmed.slice(0, trimmed.length - pattern.length - 1); // -1 for the dot before ilike
    if (!field) return undefined;
    fields.push(field);
  }
  return fields.length > 0 ? fields : undefined;
};

/** Extract the original search text from an `or` param produced by buildPostgrestOrIlikeFilter */
const extractValueFromOrParam = (orValue: string | undefined, fields: string[]): string | undefined => {
  if (!orValue || fields.length === 0) return undefined;
  // Use the first field to extract words from the or param
  const field = fields[0] as string;
  const escapedField = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Single-word single-field: (field.ilike."*word*")
  const singleFieldSingleWordRe = new RegExp(`^\\(${escapedField}\\.ilike\\."\\*(.+?)\\*"`);
  const singleMatch = singleFieldSingleWordRe.exec(orValue);
  if (singleMatch && !orValue.includes('and(')) {
    return singleMatch[1]?.replace(/""/g, '"');
  }
  // Multi-word: extract all words from the first field's and() clause
  const wordRe = new RegExp(`${escapedField}\\.ilike\\."\\*([^"]+?)\\*"`, 'g');
  const words: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = wordRe.exec(orValue)) !== null) {
    words.push(m[1]?.replace(/""/g, '"') ?? '');
  }
  return words.length > 0 ? words.join(' ') : undefined;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyValueTemplate = (value: string, template?: string): string => {
  if (!template || !template.includes(VALUE_TEMPLATE_PLACEHOLDER)) {
    return value;
  }
  // Replace every placeholder occurrence so multi-field OR templates get the search term in each slot
  return template.split(VALUE_TEMPLATE_PLACEHOLDER).join(value);
};

const buildTemplateCandidates = (template?: string): string[] => {
  if (!template) {
    return [];
  }

  const normalizedTemplate = normalizePostgrestIlikeTemplate(template, VALUE_TEMPLATE_PLACEHOLDER);
  return normalizedTemplate && normalizedTemplate !== template
    ? [normalizedTemplate, template]
    : [template];
};

const extractValueFromCandidateTemplate = (
  rawValue: string | undefined,
  template?: string,
): string | undefined => {
  if (!rawValue) {
    return undefined;
  }
  if (!template || !template.includes(VALUE_TEMPLATE_PLACEHOLDER)) {
    return rawValue;
  }

  const parts = template.split(VALUE_TEMPLATE_PLACEHOLDER);
  const escapedParts = parts.map(escapeRegExp);

  let regexString = '^' + escapedParts[0];
  if (parts.length > 1) {
    regexString += '(.*)';
    regexString += escapedParts[1];
    for (let i = 2; i < escapedParts.length; i++) {
      regexString += '\\1' + escapedParts[i];
    }
  }
  regexString += '$';

  try {
    const regex = new RegExp(regexString, 'i');
    const match = regex.exec(rawValue);
    if (!match) {
      return undefined;
    }

    const extractedValue = match[1] ?? '';
    return template.includes(QUOTED_VALUE_TEMPLATE_FRAGMENT)
      ? extractedValue.replace(/""/g, '"')
      : extractedValue;
  } catch (e) {
    logger.warn('Failed to create regex for template extraction', {
      component: 'EntityListPage',
      error: String(e),
    });
    return undefined;
  }
};

const extractValueFromTemplate = (
  rawValue: string | undefined,
  template?: string,
): string | undefined => {
  if (!rawValue) {
    return undefined;
  }

  const templateCandidates = buildTemplateCandidates(template);
  if (!templateCandidates.length) {
    return rawValue;
  }

  for (const candidate of templateCandidates) {
    const extractedValue = extractValueFromCandidateTemplate(rawValue, candidate);
    if (extractedValue !== undefined) {
      return extractedValue;
    }
  }

  return rawValue;
};

export const EntityListPage: React.FC = () => {
  const { entityType } = useParams<{ entityType: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterResetVersion, setFilterResetVersion] = useState(0);

  const searchKey = searchParams.toString();
  const filters = useMemo(() => {
    const entries: Record<string, string> = {};
    const params = new URLSearchParams(searchKey);
    params.forEach((value, key) => {
      if (value) entries[key] = value;
    });
    return entries;
  }, [searchKey]);

  const hasFilters = Object.keys(filters).length > 0;

  const { data: config, isLoading, error, isFetching } = useEntityTypeConfig(entityType!);
  const { data: uiNav } = useMenuConfig();
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const isEntityPageDisabled = isUiConfigEntityPageDisabled(config?.ui_configuration);

  // For ie_kit entities, use the configurable label from Configuration Management
  const displayName = entityType === 'ie_kit' ? ieKitLabel : config?.display_name;

  const listFiltersConfig = useMemo<EntityListFilterConfig[]>(() => {
    const defined = config?.ui_configuration?.list?.filters;
    if (Array.isArray(defined) && defined.length > 0) {
      return defined;
    }
    // Default fallback filter if none configured
    return [
      {
        key: 'defaultSearch',
        type: 'text',
        label: 'Search',
        param: 'display_name',
        valueTemplate: 'ilike."*{{value}}*"',
        placeholder: 'Search by name...',
      },
    ];
  }, [config]);

  const configuredFilterValues = useMemo(() => {
    if (!listFiltersConfig.length) {
      return {} as Record<string, string | undefined>;
    }
    return listFiltersConfig.reduce<Record<string, string | undefined>>((acc, filterConfig) => {
      // Multi-field OR ilike templates (param is 'or') — extract value directly from or param
      if (filterConfig.type === 'text' && filterConfig.param === OR_PARAM) {
        const multiFields = parseMultiFieldOrIlikeTemplate(filterConfig.valueTemplate);
        if (multiFields) {
          acc[filterConfig.key] = extractValueFromOrParam(filters[OR_PARAM], multiFields);
          return acc;
        }
      }
      // Try direct field param first (single-word case)
      const directValue = extractValueFromTemplate(
        filters[filterConfig.param],
        filterConfig.valueTemplate,
      );
      if (directValue !== undefined) {
        acc[filterConfig.key] = directValue;
        return acc;
      }
      // For text filters with simple ilike templates, also check the `or` param (multi-word case)
      if (filterConfig.type === 'text') {
        const normalizedTemplate = normalizePostgrestIlikeTemplate(
          filterConfig.valueTemplate,
          VALUE_TEMPLATE_PLACEHOLDER,
        );
        if (isSimpleIlikeTemplate(normalizedTemplate)) {
          acc[filterConfig.key] = extractValueFromOrParam(filters[OR_PARAM], [filterConfig.param]);
          return acc;
        }
      }
      acc[filterConfig.key] = undefined;
      return acc;
    }, {});
  }, [filters, listFiltersConfig]);

  const mutateSearchParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutator(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleConfiguredFilterChange = useCallback(
    (filterConfig: EntityListFilterConfig, value?: string) => {
      mutateSearchParams((next) => {
        if (!value || value.length === 0) {
          next.delete(filterConfig.param);
          next.delete(OR_PARAM);
          return;
        }
        const resolvedValue = filterConfig.type === 'text' ? value.trim() : value;
        if (!resolvedValue) {
          next.delete(filterConfig.param);
          next.delete(OR_PARAM);
          return;
        }
        const normalizedTemplate = normalizePostgrestIlikeTemplate(
          filterConfig.valueTemplate,
          VALUE_TEMPLATE_PLACEHOLDER,
        );
        // For simple ilike text filters with multiple words, use tokenized search
        if (
          filterConfig.type === 'text' &&
          isSimpleIlikeTemplate(normalizedTemplate) &&
          resolvedValue.includes(' ')
        ) {
          next.delete(filterConfig.param);
          next.set(OR_PARAM, buildPostgrestOrIlikeFilter([filterConfig.param], resolvedValue));
          return;
        }
        // Multi-field OR ilike templates: always use buildPostgrestOrIlikeFilter for tokenized search
        if (filterConfig.type === 'text') {
          const multiFields = parseMultiFieldOrIlikeTemplate(filterConfig.valueTemplate);
          if (multiFields) {
            next.delete(filterConfig.param);
            next.set(OR_PARAM, buildPostgrestOrIlikeFilter(multiFields, resolvedValue));
            return;
          }
        }
        // Single word or non-ilike template: use field-level param
        next.delete(OR_PARAM);
        const templateValue = normalizedTemplate?.includes(QUOTED_VALUE_TEMPLATE_FRAGMENT)
          ? escapePostgrestQuotedValue(resolvedValue)
          : resolvedValue;
        const formattedValue = applyValueTemplate(templateValue, normalizedTemplate);
        next.set(filterConfig.param, formattedValue);
      });
    },
    [mutateSearchParams],
  );

  const handleConfiguredFiltersReset = useCallback(() => {
    if (!listFiltersConfig.length) {
      return;
    }
    setFilterResetVersion((v) => v + 1);
    mutateSearchParams((next) => {
      listFiltersConfig.forEach((filterConfig) => next.delete(filterConfig.param));
      next.delete(OR_PARAM);
    });
  }, [listFiltersConfig, mutateSearchParams]);

  const handleClearFilters = useCallback(() => {
    if (!entityType) return;
    navigate(`/${entityType}`);
  }, [navigate, entityType]);

  if (isLoading || isFetching || !config) {
    return <LoadingSkeleton variant="grid" count={8} />;
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 2 }}>
        <Alert severity="error">Entity type &quot;{entityType}&quot; not found or not configured.</Alert>
      </Container>
    );
  }

  if (isEntityPageDisabled) {
    return <NotFoundPage />;
  }

  return (
    <Container maxWidth={false} sx={{ pt: 1, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      <Box sx={{ mb: 0.5, flexShrink: 0 }}>
        <Breadcrumbs sx={{ mb: 0 }}>
          {(() => {
            const parts: React.ReactNode[] = [];
            parts.push(
              <Link
                key="home"
                color="inherit"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                Home
              </Link>,
            );
            const groupKey = config.ui_configuration?.group;
            const groupName = groupKey ? uiNav?.groups?.[groupKey]?.display_name : undefined;
            if (groupName) {
              parts.push(
                <Typography key="group" color="text.primary">
                  {groupName}
                </Typography>,
              );
            }
            parts.push(
              <Typography key="type" color="text.primary">
                {displayName}
              </Typography>,
            );
            return parts;
          })()}
        </Breadcrumbs>

        {config.description && (
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>
        )}
      </Box>

      {listFiltersConfig.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <EntityListFilters
            filters={listFiltersConfig}
            values={configuredFilterValues}
            onChange={handleConfiguredFilterChange}
            onClear={handleConfiguredFiltersReset}
            resetVersion={filterResetVersion}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {entityType === 'asset' ? (
          <AssetInventoryList filters={filters} />
        ) : (
          <EntityList
            entityType={entityType!}
            entityConfig={config}
            title={`${displayName} Entities`}
            filters={hasFilters ? filters : undefined}
            onClearFilters={hasFilters ? handleClearFilters : undefined}
            onEntityClick={(entity) =>
              navigate(`/${entityType}/${entity.id}`, {
                state: { returnSearch: searchParams.toString() },
              })
            }
          />
        )}
      </Box>
    </Container>
  );
};
